import { X, MapPin, Camera, Wifi, WifiOff, CheckCircle2, RotateCcw, RefreshCw, AlertTriangle, Package } from 'lucide-react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useGeolocation } from '../hooks/useGeolocation';
import { useOnRoadReporting } from '../hooks/useOnRoadReporting';
import { toast } from 'react-toastify';
import '../../../css/OnRoad.css';

export default function EventReportingSheet({ 
    isOpen, 
    onClose, 
    fase, 
    evento, 
    manifiesto, 
    placa, 
    onSubmit,
    operacionData,
    remesasSeleccionadas
}) {
    const [observaciones, setObservaciones] = useState('');
    const [fileupload, setFileupload] = useState(null);
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [facingMode, setFacingMode] = useState('environment');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const { 
        coords, 
        loading: geoLoading,
        error: geoError, 
        recapture: recaptureLocation,
        isLocationValid
    } = useGeolocation();

    const { isReporting, reportarEventoFlujo } = useOnRoadReporting();

    const online = navigator.onLine;

    // Función para formatear fecha y hora
    const formatDateTime = (date) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
               `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    // Determinar si el formulario es válido para envío
    const isFormValid = () => {
        return isLocationValid && !isReporting && online;
    };

    // Iniciar cámara
    const startCamera = useCallback(async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode }
            });
            streamRef.current = mediaStream;
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play();
            }
        } catch (err) {
            console.error('Error cámara:', err);
            toast.error('No se pudo acceder a la cámara');
        }
    }, [facingMode]);

    // Detener cámara
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    }, []);

    // Effect para manejar la cámara cuando se abre/cierra el modal
    useEffect(() => {
        if (showCameraModal) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [showCameraModal, startCamera, stopCamera]);

    // Effect para recapturar ubicación cuando se abre el modal
    useEffect(() => {
        if (isOpen && !isLocationValid && !geoLoading) {
            recaptureLocation();
        }
    }, [isOpen, isLocationValid, geoLoading, recaptureLocation]);

    // Capturar foto con timestamp
    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;

        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Superponer fecha y hora
        const fh = formatDateTime(new Date());
        ctx.font = '20px Arial';
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.strokeText(fh, 10, canvas.height - 20);
        ctx.fillText(fh, 10, canvas.height - 20);

        canvas.toBlob(blob => {
            if (!blob) return;
            const file = new File([blob], `evidencia-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFileupload(file);
            toast.success('Imagen capturada correctamente');
            setShowCameraModal(false);
        }, 'image/jpeg', 0.9);
    };

    // Eliminar foto
    const eliminarFoto = () => {
        setFileupload(null);
        toast.info('Foto eliminada. Puede tomar una nueva foto.');
    };

    // Alternar cámara frontal/trasera
    const toggleCamera = () => {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    };

    // Manejar intento de ubicación manual
    const handleRetryLocation = () => {
        if (geoLoading) return;
        toast.info('Obteniendo ubicación GPS...');
        recaptureLocation();
    };

    const handleClose = () => {
        setObservaciones('');
        setFileupload(null); // Limpiar foto al cerrar
        stopCamera(); // Asegurar que se detiene la cámara
        onClose();
    };

    const handleSubmit = async () => {
        // Validación estricta antes del envío
        if (!isLocationValid) {
            toast.error('Se requiere ubicación GPS válida para reportar el evento');
            return;
        }

        if (!online) {
            toast.error('Se requiere conexión a internet para reportar el evento');
            return;
        }
        if (isReporting) return;
        
        try {
            const operacionDataFiltrada = remesasSeleccionadas ? {
                ...operacionData,
                remesas: remesasSeleccionadas.remesas
            } : operacionData;

            // 1. Reportar usando el servicio centralizado (incluye ambos endpoints)
            const resultado = await reportarEventoFlujo({
                fase,
                evento,
                observaciones: observaciones.trim() || `Evento reportado: ${evento} ${fase}`,
                evidencias: fileupload ? [fileupload] : [],
                coords,
                manifiesto,
                operacionData: operacionDataFiltrada,
                remesa: remesasSeleccionadas?.remesas?.length === 1 ? 
                       remesasSeleccionadas.remesas[0].id : null
            });

            // 2. Si fue exitoso, también llamar al callback del sistema interno para actualizar UI
            if (resultado?.success) {
                handleClose();
                await onSubmit({
                    ...resultado,
                    fase,
                    evento,
                    remesasAfectadas: remesasSeleccionadas,
                    requiresRevalidation: true // Flag para indicar que se necesita re-fetch
                });
            } else {
                console.error('Error al reportar evento:', resultado.message);
            }
        } catch (error) {
            console.error('Error inesperado al reportar evento:', error);
            toast.error('Error inesperado al reportar evento');
        }
    };

    const getEventIcon = (eventoType) => {
        const icons = {
            'LLEGADA': '📍',
            'SALIDA': '🏁'
        };
        return icons[eventoType] || '📍';
    };

    const getEventTitle = (eventoType) => {
        const titles = {
            'LLEGADA': 'Llegada',
            'SALIDA': 'Salida'
        };
        return titles[eventoType] || eventoType;
    };

    // Nueva función para obtener la ciudad según la fase
    const getCiudadEvento = () => {
        // Prioridad 1: Si hay remesas seleccionadas, usar su ciudad
        if (remesasSeleccionadas?.ciudad) {
            return remesasSeleccionadas.ciudad;
        }

        // Prioridad 2: Extraer de la primera remesa en operacionData
        if (operacionData?.remesas && operacionData.remesas.length > 0) {
            const primeraRemesa = operacionData.remesas[0];
            
            if (fase === 'CARGUE') {
                return primeraRemesa.origen;
            } else if (fase === 'DESCARGUE') {
                return primeraRemesa.destino;
            }
        }

        // Fallback: retornar null si no hay datos
        return null;
    };

    if (!isOpen) return null;

    // Mostrar información de remesas seleccionadas en el header
    const renderRemesasInfo = () => {
        if (!remesasSeleccionadas) return null;

        const { remesas } = remesasSeleccionadas;
        const cantidadRemesas = remesas.length;
        // const ubicacionLabel = fase === 'CARGUE' ? 'Origen' : 'Destino';

        const getRemesaIdentifier = (remesa) => {
            // Prioridad: consecutivo > id_remesa > id
            if (remesa.consecutivo && remesa.consecutivo.trim() !== '') {
                return remesa.consecutivo;
            }
            if (remesa.id_remesa) {
                return `#${remesa.id_remesa}`;
            }
            return `#${remesa.id}`;
        };

        // Caso 1: Una sola remesa - Mostrar con identificador completo
        if (cantidadRemesas === 1) {
            const remesa = remesas[0];
            const identificador = getRemesaIdentifier(remesa);
            
            return (
                <div className="remesas-selected-info">
                    <div className="remesas-badge single">
                        <Package size={18} />
                        <div className="remesa-info-content">
                            <span className="remesa-count">1 remesa seleccionada</span>
                            <span className="remesa-identifier">{identificador}</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Caso 2: Múltiples remesas - Mostrar conteo y primeros identificadores
        const primerasRemesas = remesas.slice(0, 2);
        const identificadores = primerasRemesas.map(r => getRemesaIdentifier(r));
        const mostrarMas = cantidadRemesas > 2;

        return (
            <div className="remesas-selected-info">
                <div className="remesas-badge multiple">
                    <Package size={18} />
                    <div className="remesa-info-content">
                        <span className="remesa-count">
                            {cantidadRemesas} remesas seleccionadas
                        </span>
                        <span className="remesa-identifiers">
                            {identificadores.join(', ')}
                            {mostrarMas && ` +${cantidadRemesas - 2} más`}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="sheet-backdrop">
            <div className="sheet-container">
                <div className="sheet-header">
                    <div className="sheet-title-container">
                        <div className="sheet-title-main">
                            <span className="event-emoji">{getEventIcon(evento)}</span>
                            <span className="title-text">
                                Reportar {getEventTitle(evento)} {fase}
                            </span>
                        </div>
                        {getCiudadEvento() && (
                            <div className="sheet-title-location">
                                <MapPin size={14} />
                                <span>{getCiudadEvento()}</span>
                            </div>
                        )}
                    </div>
                    <button 
                        className="sheet-close-btn"
                        onClick={handleClose}
                        disabled={isReporting}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="sheet-content event">
                    {renderRemesasInfo()}
                    {/* Información del evento - COMPACTA */}
                    <div className="event-info-section-compact">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Evento</span>
                                <span className="info-value">{getEventTitle(evento)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Fase</span>
                                <span className="info-value">{fase}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Manifiesto</span>
                                <span className="info-value">{manifiesto}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Placa</span>
                                <span className="info-value">{placa}</span>
                            </div>
                        </div>
                    </div>

                    {/* Estado de conectividad y GPS */}
                    <div className="connectivity-section-compact">
                        <div className="connectivity-item">
                            {online ? <Wifi size={14} className="text-success" /> : <WifiOff size={14} className="text-danger" />}
                            <span>{online ? 'En línea' : 'Sin conexión'}</span>
                        </div>
                        <div className="connectivity-item">
                            {geoLoading ? (
                                <>
                                    <RefreshCw size={14} className="text-warning animate-spin" />
                                    <span>Obteniendo GPS...</span>
                                </>
                            ) : isLocationValid ? (
                                <>
                                    <MapPin size={14} className="text-success" />
                                    <span>GPS (±{coords.accuracy}m)</span>
                                </>
                            ) : (
                                <>
                                    <AlertTriangle size={14} className="text-danger" />
                                    <span>GPS requerido</span>
                                    <button 
                                        className="retry-location-btn"
                                        onClick={handleRetryLocation}
                                        disabled={geoLoading}
                                        title="Reintentar obtener ubicación"
                                    >
                                        <RefreshCw size={12} className={geoLoading ? 'animate-spin' : ''} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Mensaje de error de GPS */}
                    {geoError && (
                        <div className="gps-error-message">
                            <AlertTriangle size={16} className="text-danger" />
                            <span>{geoError}</span>
                            <button 
                                className="retry-location-btn-text"
                                onClick={handleRetryLocation}
                                disabled={geoLoading}
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {/* Evidencia fotográfica */}
                    <div className="photo-section-compact">
                        <label className="section-label-compact">Evidencia Fotográfica</label>
                        
                        {!fileupload ? (
                            <button 
                                className="photo-button-full"
                                type="button"
                                onClick={() => setShowCameraModal(true)}
                            >
                                <Camera size={16} />
                                Tomar Foto
                            </button>
                        ) : (
                            <div className="photo-preview-enhanced">
                                {/* Vista previa de la imagen más grande */}
                                <div className="photo-preview-container">
                                    <img 
                                        src={URL.createObjectURL(fileupload)} 
                                        alt="Evidencia capturada"
                                        className="photo-preview-image"
                                    />
                                    <button
                                        type="button"
                                        className="photo-quick-remove"
                                        onClick={eliminarFoto}
                                        title="Eliminar foto"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                                
                                {/* Botones de acción reorganizados */}
                                <div className="photo-actions-container">
                                    <button 
                                        className="photo-action-btn secondary"
                                        type="button"
                                        onClick={() => setShowCameraModal(true)}
                                    >
                                        <Camera size={14} />
                                        Tomar Nueva Foto
                                    </button>
                                    
                                    <button 
                                        className="photo-action-btn danger"
                                        type="button"
                                        onClick={eliminarFoto}
                                    >
                                        <X size={14} />
                                        Eliminar Foto
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal de Cámara */}
                    {showCameraModal && (
                        <div className="sheet-backdrop" style={{ zIndex: 9999 }}>
                            <div className="camera-modal-container">
                                <div className="camera-modal-header">
                                    <div className="camera-modal-title">
                                        <Camera size={20} />
                                        Capturar Evidencia
                                    </div>
                                    <button 
                                        className="camera-close-btn"
                                        onClick={() => setShowCameraModal(false)}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                
                                <div className="camera-modal-content">
                                    <div className="camera-viewport">
                                        <video
                                            ref={videoRef}
                                            autoPlay
                                            playsInline
                                            className="camera-video"
                                        />
                                    </div>
                                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                                </div>
                                
                                <div className="camera-modal-footer">
                                    <button
                                        className="camera-control-btn secondary"
                                        onClick={toggleCamera}
                                        title="Cambiar cámara"
                                    >
                                        <RotateCcw size={16} />
                                        <span>Rotar</span>
                                    </button>
                                    
                                    <button
                                        className="camera-control-btn primary capture"
                                        onClick={capturePhoto}
                                    >
                                        <Camera size={20} />
                                        <span>Capturar</span>
                                    </button>
                                    
                                    <button
                                        className="camera-control-btn secondary"
                                        onClick={() => setShowCameraModal(false)}
                                        title="Cancelar"
                                    >
                                        <X size={16} />
                                        <span>Cancelar</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Observaciones - COMPACTAS */}
                    <div className="observations-section-compact">
                        <label className="section-label-compact">Observaciones</label>
                        <textarea
                            className="observations-textarea-compact"
                            placeholder="Detalles opcionales..."
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            maxLength={280}
                            rows={2}
                        />
                        <div className="char-count-compact">
                            {observaciones.length}/280
                        </div>
                    </div>
                </div>

                <div className="sheet-footer">
                    <button
                        className={`btn-report-event ${!isFormValid() ? 'disabled' : ''}`}
                        onClick={handleSubmit}
                        disabled={!isFormValid()}
                        title={!isLocationValid ? 'Se requiere ubicación GPS válida' : 
                               !online ? 'Se requiere conexión a internet' : 
                               'Reportar evento'}
                    >
                        {isReporting ? (
                            <>
                                <div className="spinner-small"></div>
                                Reportando...
                            </>
                        ) : !isLocationValid ? (
                            <>
                                <AlertTriangle size={18} />
                                GPS Requerido
                            </>
                        ) : !online ? (
                            <>
                                <WifiOff size={18} />
                                Sin Conexión
                            </>
                        ) : (
                            <>
                                <CheckCircle2 size={18} />
                                Reportar Evento
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}